import { t } from "@lingui/macro";
import type { Caller } from "@repo/common";
import { db, updateCaller } from "@repo/ui";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useData } from "../contexts/DataContext";
import { useCallers } from "../hooks/useCallers";
import {
	callerDate,
	customerPhone,
	matchesCaller,
	phoneKey,
} from "../utils/callerDirectory";
import { CallerInstructionsCard } from "./CallerInstructionsCard";
import { CallerMemoryCard } from "./CallerMemoryCard";
import { CallsPage } from "./CallsPage";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";

export function CallersPage({
	callerId,
	onCallerSelect,
	selectedCallId,
	onCallSelect,
}: {
	selectedCallId: string | null;
	onCallSelect: (id: string | null) => void;
	callerId: string | null;
	onCallerSelect: (id: string | null) => void;
}) {
	const { user, calls, callsLoading, callsError, language } = useData();
	const { callers, loading, error } = useCallers(
		user?.companyId,
		callerId ?? undefined,
	);
	const [search, setSearch] = useState("");
	const [tags, setTags] = useState<string[]>([]);
	const [from, setFrom] = useState("");
	const [to, setTo] = useState("");
	const [sort, setSort] = useState("recent");
	const [page, setPage] = useState(1);
	const callId = selectedCallId;
	const setCallId = onCallSelect;
	// Reset pagination whenever the active query or profile changes.
	// biome-ignore lint/correctness/useExhaustiveDependencies: these values intentionally trigger a reset.
	useEffect(() => setPage(1), [search, tags, from, to, sort, callerId]);

	const availableTags = [
		...new Set(callers.flatMap((c) => c.tags || [])),
	].sort();
	const filtered = useMemo(
		() =>
			callers
				.filter((c) => {
					const date = callerDate(c.lastCallAt);
					const day = date
						? [
								date.getFullYear(),
								String(date.getMonth() + 1).padStart(2, "0"),
								String(date.getDate()).padStart(2, "0"),
							].join("-")
						: "";
					return (
						matchesCaller(c, search) &&
						(!tags.length || tags.some((tag) => c.tags?.includes(tag))) &&
						(!from || (!!day && day >= from)) &&
						(!to || (!!day && day <= to))
					);
				})
				.sort((a, b) =>
					sort === "name"
						? (a.name || "").localeCompare(b.name || "", language)
						: sort === "count"
							? (b.callCount || 0) - (a.callCount || 0)
							: (callerDate(b.lastCallAt)?.getTime() || 0) -
								(callerDate(a.lastCallAt)?.getTime() || 0),
				),
		[callers, search, tags, from, to, sort, language],
	);
	const caller = callerId ? callers.find((c) => c.id === callerId) : null;
	const history = caller
		? calls.filter(
				(c) =>
					!c.piiRemovalPending &&
					phoneKey(customerPhone(c)) === phoneKey(caller.phoneNumber),
			)
		: [];
	const dateText = (date: unknown) =>
		callerDate(date)?.toLocaleDateString(language) || "—";
	if (callId)
		return <CallsPage selectedCallId={callId} onCallSelect={setCallId} />;
	return (
		<div className="mx-auto max-w-7xl space-y-6 px-4 sm:px-6 lg:px-8">
			{callerId && (
				<Button variant="ghost" onClick={() => onCallerSelect(null)}>
					{language === "he" ? (
						<ArrowRight className="w-4 h-4 ml-2" />
					) : (
						<ArrowLeft className="w-4 h-4 mr-2" />
					)}
					{t({ id: "backToCallers", message: "Back to callers" })}
				</Button>
			)}

			<div className="flex flex-wrap items-center justify-between gap-3">
				<h1 className="text-2xl font-semibold">
					{callerId
						? caller?.name ||
							caller?.phoneNumber ||
							t({ id: "callerProfile", message: "Caller profile" })
						: t({ id: "callers", message: "Callers" })}
				</h1>
			</div>
			{loading ? (
				<p role="status">{t({ id: "loading", message: "Loading..." })}</p>
			) : error ? (
				<p role="alert">
					{t({
						id: "callersLoadError",
						message: "Could not load callers. Reopen this page to try again.",
					})}
				</p>
			) : callerId ? (
				caller ? (
					<>
						<div className="grid gap-6 lg:grid-cols-2">
							<CallerEditor key={caller.id} caller={caller} />
							<dl className="grid grid-cols-2 content-start gap-4 rounded-xl border border-border bg-card text-card-foreground p-6">
								{[
									[
										t({ id: "firstContact", message: "First contact" }),
										dateText(caller.firstCallAt),
									],
									[
										t({ id: "lastContact", message: "Last contact" }),
										dateText(caller.lastCallAt),
									],
									[
										t({ id: "callCount", message: "Call count" }),
										caller.callCount || 0,
									],
									[
										t({
											id: "preferredLanguage",
											message: "Preferred language",
										}),
										caller.preferredLanguage || "—",
									],
								].map(([label, value]) => (
									<div key={label}>
										<dt className="text-sm text-muted-foreground">{label}</dt>
										<dd>{value}</dd>
									</div>
								))}
							</dl>
						</div>
						<section className="rounded-xl border border-border bg-card text-card-foreground p-6 empty:hidden [&>div]:border-t-0 [&>div]:pt-0">
							<CallerMemoryCard
								companyId={user!.companyId!}
								phoneNumber={caller.phoneNumber}
								language={language}
							/>
						</section>
						<section className="rounded-xl border border-border bg-card text-card-foreground p-6 empty:hidden [&>div]:border-t-0 [&>div]:pt-0">
							<CallerInstructionsCard
								companyId={user!.companyId!}
								phoneNumber={caller.phoneNumber}
								language={language}
							/>
						</section>
						<section className="space-y-4 rounded-xl border border-border bg-card text-card-foreground p-6">
							<h2 className="text-xl font-semibold">
								{t({ id: "callerCallHistory", message: "Call history" })}
							</h2>
							{callsError ? (
								<p role="alert">
									{t({
										id: "callerHistoryError",
										message: "Could not load call history.",
									})}
								</p>
							) : callsLoading ? (
								<p role="status">
									{t({ id: "loading", message: "Loading..." })}
								</p>
							) : (
								<>
									<div className="divide-y divide-border">
										{history.slice((page - 1) * 20, page * 20).map((call) => (
											<button
												key={call.id}
												className="flex w-full flex-wrap items-center justify-between gap-2 py-4 text-start hover:bg-muted focus-visible:outline-2"
												onClick={() => setCallId(call.id)}
											>
												<span>{call.title || call.agent}</span>
												<span className="text-sm text-muted-foreground">
													{dateText(call.occurredAt || call.date)} ·{" "}
													{call.isOutbound || call.direction === "outbound"
														? t({ id: "outbound", message: "Outbound" })
														: t({ id: "inbound", message: "Inbound" })}{" "}
													· {call.duration}
												</span>
											</button>
										))}
									</div>
									{!history.length && (
										<p>
											{t({
												id: "noCallerCalls",
												message: "No available calls for this caller.",
											})}
										</p>
									)}
									<Pagination
										page={page}
										total={history.length}
										setPage={setPage}
									/>
								</>
							)}
						</section>
					</>
				) : (
					<p>
						{t({
							id: "callerNotFound",
							message: "Caller not found in this company.",
						})}
					</p>
				)
			) : (
				<>
					<div className="flex flex-wrap items-end gap-4">
						<label className="min-w-56 flex-1 space-y-2">
							<span className="text-sm">
								{t({
									id: "searchCallers",
									message: "Search by name, phone, or email",
								})}
							</span>
							<Input
								value={search}
								onChange={(e) => setSearch(e.target.value)}
							/>
						</label>
						<label className="space-y-2">
							<span className="text-sm">
								{t({ id: "contactFrom", message: "Last contact from" })}
							</span>
							<Input
								type="date"
								value={from}
								onChange={(e) => setFrom(e.target.value)}
							/>
						</label>
						<label className="space-y-2">
							<span className="text-sm">
								{t({ id: "contactTo", message: "Last contact to" })}
							</span>
							<Input
								type="date"
								value={to}
								onChange={(e) => setTo(e.target.value)}
							/>
						</label>
						<label className="space-y-2">
							<span className="block text-sm">
								{t({ id: "sortCallers", message: "Sort callers" })}
							</span>
							<select
								className="h-10 rounded-md border border-input bg-background px-3"
								value={sort}
								onChange={(e) => setSort(e.target.value)}
							>
								<option value="recent">
									{t({ id: "lastContact", message: "Last contact" })}
								</option>
								<option value="name">
									{t({ id: "name", message: "Name" })}
								</option>
								<option value="count">
									{t({ id: "callCount", message: "Call count" })}
								</option>
							</select>
						</label>
					</div>
					{!!availableTags.length && (
						<fieldset className="flex flex-wrap gap-3">
							<legend className="mb-2 text-sm">
								{t({ id: "tags", message: "Tags" })}
							</legend>
							{availableTags.map((tag) => (
								<label key={tag} className="flex items-center gap-2 text-sm">
									<input
										type="checkbox"
										checked={tags.includes(tag)}
										onChange={() =>
											setTags((old) =>
												old.includes(tag)
													? old.filter((x) => x !== tag)
													: [...old, tag],
											)
										}
									/>
									{tag}
								</label>
							))}
						</fieldset>
					)}
					<div className="overflow-x-auto rounded-xl border border-border bg-card text-card-foreground">
						<table className="w-full text-start text-sm">
							<thead className="bg-muted">
								<tr>
									{[
										t({ id: "name", message: "Name" }),
										t({ id: "phone", message: "Phone" }),
										t({ id: "email", message: "Email" }),
										t({ id: "tags", message: "Tags" }),
										t({ id: "callCount", message: "Call count" }),
										t({ id: "lastContact", message: "Last contact" }),
									].map((label) => (
										<th
											key={label}
											className="px-4 py-3 text-start font-medium"
										>
											{label}
										</th>
									))}
								</tr>
							</thead>
							<tbody className="divide-y divide-border">
								{filtered.slice((page - 1) * 20, page * 20).map((c) => (
									<tr key={c.id} className="hover:bg-muted/50">
										<td className="px-4 py-4">
											<button
												className="font-medium text-primary underline underline-offset-4"
												onClick={() => onCallerSelect(c.id!)}
											>
												{c.name || c.phoneNumber}
											</button>
										</td>
										<td className="px-4 py-4">
											<bdi dir="ltr">{c.phoneNumber}</bdi>
										</td>
										<td className="px-4 py-4">{c.email || "—"}</td>
										<td className="px-4 py-4">{c.tags?.join(", ") || "—"}</td>
										<td className="px-4 py-4">{c.callCount || 0}</td>
										<td className="px-4 py-4">{dateText(c.lastCallAt)}</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
					{!filtered.length && (
						<p>
							{t({
								id: "noMatchingCallers",
								message: "No callers match these filters.",
							})}
						</p>
					)}
					<Pagination page={page} total={filtered.length} setPage={setPage} />
				</>
			)}
		</div>
	);
}
function Pagination({
	page,
	total,
	setPage,
}: {
	page: number;
	total: number;
	setPage: (page: number) => void;
}) {
	return (
		<div className="flex items-center justify-end gap-3">
			<Button
				variant="outline"
				disabled={page <= 1}
				onClick={() => setPage(page - 1)}
			>
				{t({ id: "previous", message: "Previous" })}
			</Button>
			<span className="text-sm tabular-nums">
				{page} / {Math.max(1, Math.ceil(total / 20))}
			</span>
			<Button
				variant="outline"
				disabled={page * 20 >= total}
				onClick={() => setPage(page + 1)}
			>
				{t({ id: "next", message: "Next" })}
			</Button>
		</div>
	);
}
function CallerEditor({ caller }: { caller: Caller }) {
	const [editing, setEditing] = useState(false);
	const [draft, setDraft] = useState({
		name: caller.name || "",
		email: caller.email || "",
		tags: (caller.tags || []).join(", "),
		notes: caller.notes || "",
	});
	const [original, setOriginal] = useState(draft);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState(false);
	return (
		<form
			className="space-y-4 rounded-xl border border-border bg-card text-card-foreground p-6"
			onSubmit={async (e) => {
				e.preventDefault();
				setSaving(true);
				setError(false);
				try {
					const changes: Partial<Caller> = {};
					if (draft.name !== original.name) changes.name = draft.name;
					if (draft.email !== original.email)
						changes.email = draft.email || null;
					if (draft.tags !== original.tags)
						changes.tags = draft.tags
							.split(",")
							.map((x) => x.trim())
							.filter(Boolean);
					if (draft.notes !== original.notes)
						changes.notes = draft.notes || null;
					if (Object.keys(changes).length)
						await updateCaller(
							db,
							caller.companyId,
							caller.phoneNumber,
							changes,
						);
					setEditing(false);
				} catch {
					setError(true);
				} finally {
					setSaving(false);
				}
			}}
		>
			<p dir="ltr" className="text-start">
				{caller.phoneNumber}
			</p>
			{(
				[
					["name", t({ id: "name", message: "Name" })],
					["email", t({ id: "email", message: "Email" })],
					["tags", t({ id: "tags", message: "Tags" })],
					["notes", t({ id: "notes", message: "Notes" })],
				] as const
			).map(([key, label]) => (
				<label key={key} className="block space-y-2">
					<span className="text-sm font-medium">{label}</span>
					{editing ? (
						key === "notes" ? (
							<Textarea
								value={draft[key]}
								onChange={(e) => setDraft({ ...draft, [key]: e.target.value })}
							/>
						) : (
							<Input
								type={key === "email" ? "email" : "text"}
								value={draft[key]}
								onChange={(e) => setDraft({ ...draft, [key]: e.target.value })}
							/>
						)
					) : (
						<p dir="auto" className="whitespace-pre-wrap">
							{key === "tags"
								? caller.tags?.join(", ") || "—"
								: caller[key] || "—"}
						</p>
					)}
				</label>
			))}
			{error && (
				<p role="alert">
					{t({
						id: "callerSaveError",
						message: "Could not save caller details. Please try again.",
					})}
				</p>
			)}
			{editing ? (
				<div className="flex gap-2">
					<Button disabled={saving} type="submit">
						{t({ id: "save", message: "Save" })}
					</Button>
					<Button
						disabled={saving}
						variant="outline"
						type="button"
						onClick={() => setEditing(false)}
					>
						{t({ id: "cancel", message: "Cancel" })}
					</Button>
				</div>
			) : (
				<Button
					type="button"
					variant="outline"
					onClick={() => {
						setOriginal({
							name: caller.name || "",
							email: caller.email || "",
							tags: (caller.tags || []).join(", "),
							notes: caller.notes || "",
						});
						setError(false);
						setDraft({
							name: caller.name || "",
							email: caller.email || "",
							tags: (caller.tags || []).join(", "),
							notes: caller.notes || "",
						});
						setEditing(true);
					}}
				>
					{t({ id: "editCallerInfo", message: "Edit Caller Info" })}
				</Button>
			)}
		</form>
	);
}
